'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChainId } from '@morpho-org/blue-sdk';
import type { Address } from 'viem';
import { isDefined } from '@morpho-org/morpho-ts';
import { CursorPagination } from '@morpho-org/ui-v6/client';
import { TableNoResults } from '@morpho-org/ui-v6/server';
import { useTable } from '@tanstack/react-table';
import { startTransition, useState } from 'react';
import { DateRangeFilter, dateRangeToTimestamps, type DateRangeValue } from '@/components/common/DateRangeFilter';
import { ErrorDataUnavailable } from '@/components/common/Error';
import { Table, TrBodyRow, TrHeaderGroup } from '@/components/common/Table';
import { TableScrollArea } from '@/components/common/Table/CompactTable/Table';
import { TransactionTypeFilter } from '@/components/common/TransactionTypeFilter';
import { VaultTransactionsLayout } from '@/components/VaultPage/components/VaultTransactionsLayout';
import { VAULT_TABLE_10_ROWS_HEIGHT } from '@/components/VaultPage/config';
import { VaultTransactionsPartialNotice } from '@/components/VaultPage/vaultTransactions/components/VaultTransactionsPartialNotice';
import { getVaultTransactionsColumns, LEGACY_TO_VAULT_TRANSACTION_TYPE, PAGE_SIZE, TRANSACTION_FILTER_OPTIONS, VAULT_TRANSACTION_TYPE_LABELS } from '@/components/VaultPage/vaultTransactions/config';
import { useVaultTransactionsTableData } from '@/components/VaultPage/vaultTransactions/hooks/useVaultTransactionsTableData';
import { toVaultTransactionsTableRows } from '@/components/VaultPage/vaultTransactions/toVaultTransactionsTableRows';
import { useWeb3Context } from '@/contexts/Web3Context';
import { useIsBelowBreakpoint } from '@/hooks/useBreakpoints';
import { useSkeletonRows } from '@/hooks/useSkeletonRows';
import { TransactionType } from '@/types/legacy';
import { vaultTransactionsTableFeatures } from '../../../vaultTransactions/config/VaultTransactions.columns';
export interface VaultYourTransactionsClientProps {
  address: Address;
  chainId: ChainId;
}
const columnsDefs = getVaultTransactionsColumns();
const VaultYourTransactionsClient = t0 => {
  const $ = _c(67);
  const {
    address,
    chainId
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [yourTransactionTypeFilter, setYourTransactionTypeFilter] = useState(t1);
  const [dateRange, setDateRange] = useState();
  let t2;
  if ($[1] !== dateRange) {
    t2 = dateRangeToTimestamps(dateRange);
    $[1] = dateRange;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    timestampGte,
    timestampLte
  } = t2;
  const {
    account
  } = useWeb3Context();
  let t3;
  if ($[3] !== yourTransactionTypeFilter) {
    t3 = yourTransactionTypeFilter.map(_temp).filter(isDefined);
    $[3] = yourTransactionTypeFilter;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const activeTransactionTypes = t3;
  const t4 = account.address ?? null;
  let t5;
  if ($[5] !== t4) {
    t5 = {
      kind: "viewer",
      userAddress: t4
    };
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== activeTransactionTypes || $[8] !== address || $[9] !== chainId || $[10] !== t5 || $[11] !== timestampGte || $[12] !== timestampLte) {
    t6 = {
      address,
      chainId,
      vaultVersion: "v1",
      limit: PAGE_SIZE,
      filteredTransactionTypes: activeTransactionTypes,
      typeMap: LEGACY_TO_VAULT_TRANSACTION_TYPE,
      scope: t5,
      timestampGte,
      timestampLte
    };
    $[7] = activeTransactionTypes;
    $[8] = address;
    $[9] = chainId;
    $[10] = t5;
    $[11] = timestampGte;
    $[12] = timestampLte;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const {
    page,
    isLoading,
    isLoadingNextPage,
    error,
    pageIndex,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    prefetchNext
  } = useVaultTransactionsTableData(t6);
  const isMediumScreen = useIsBelowBreakpoint("m");
  let t7;
  if ($[14] !== chainId || $[15] !== page) {
    t7 = toVaultTransactionsTableRows({
      page,
      chainId,
      typeLabels: VAULT_TRANSACTION_TYPE_LABELS
    });
    $[14] = chainId;
    $[15] = page;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const formattedTransactions = t7;
  let t8;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = selectedTransactionTypes => {
      startTransition(() => {
        setYourTransactionTypeFilter(selectedTransactionTypes);
      });
    };
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const handleFilterChange = t8;
  let t9;
  if ($[18] !== formattedTransactions) {
    t9 = {
      features: vaultTransactionsTableFeatures,
      data: formattedTransactions,
      columns: columnsDefs,
      enableSorting: false
    };
    $[18] = formattedTransactions;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const transactionsTable = useTable(t9);
  const loading = isLoading || isLoadingNextPage;
  let t10;
  if ($[20] !== loading || $[21] !== transactionsTable) {
    t10 = !loading && transactionsTable.getRowModel().rows.length === 0;
    $[20] = loading;
    $[21] = transactionsTable;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const hasNoTransactions = t10;
  const partial = page?.partial ?? false;
  const unavailable = error != null || partial && hasNoTransactions;
  let t11;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      first: PAGE_SIZE,
      totalCount: PAGE_SIZE,
      pagination: {
        pageIndex: 0,
        pageSize: PAGE_SIZE
      },
      columnsDefs
    };
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const loadingRows = useSkeletonRows(t11);
  let t12;
  if ($[24] !== dateRange) {
    t12 = <DateRangeFilter value={dateRange} onChange={setDateRange} side="bottom" align="end" page="vault_your_transactions" />;
    $[24] = dateRange;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== isMediumScreen || $[27] !== yourTransactionTypeFilter) {
    t13 = isMediumScreen ? <div className="gap-xs flex flex-row items-center"><span className="text-3xs text-foreground">Type:</span><TransactionTypeFilter selectedTypes={yourTransactionTypeFilter} onFilterChange={handleFilterChange} options={TRANSACTION_FILTER_OPTIONS} page="vault_your_transactions" /></div> : <TransactionTypeFilter selectedTypes={yourTransactionTypeFilter} onFilterChange={handleFilterChange} options={TRANSACTION_FILTER_OPTIONS} page="vault_your_transactions" />;
    $[26] = isMediumScreen;
    $[27] = yourTransactionTypeFilter;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t12 || $[30] !== t13) {
    t14 = <div className="gap-sm max-lg:gap-xs flex flex-row flex-wrap items-center">{t12}{t13}</div>;
    $[29] = t12;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== hasNoTransactions || $[33] !== loading || $[34] !== partial) {
    t15 = partial && !hasNoTransactions && !loading && <VaultTransactionsPartialNotice />;
    $[32] = hasNoTransactions;
    $[33] = loading;
    $[34] = partial;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== hasNoTransactions || $[37] !== loading || $[38] !== loadingRows || $[39] !== transactionsTable) {
    t16 = (!hasNoTransactions || loading) && <Table.Table tableVariant="compact"><Table.Thead>{transactionsTable.getHeaderGroups().map(_temp2)}</Table.Thead><Table.Tbody>{!loading && transactionsTable.getRowModel().rows.map(_temp3)}{loading && loadingRows.map(_temp4)}</Table.Tbody></Table.Table>;
    $[36] = hasNoTransactions;
    $[37] = loading;
    $[38] = loadingRows;
    $[39] = transactionsTable;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== hasNoTransactions || $[42] !== unavailable) {
    t17 = hasNoTransactions && !unavailable && <TableNoResults text="No transactions found." height={VAULT_TABLE_10_ROWS_HEIGHT} style={{
      borderRadius: "0"
    }} />;
    $[41] = hasNoTransactions;
    $[42] = unavailable;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== loading || $[45] !== unavailable) {
    t18 = !loading && unavailable && <ErrorDataUnavailable minHeight={VAULT_TABLE_10_ROWS_HEIGHT} style={{
      borderRadius: "0"
    }} />;
    $[44] = loading;
    $[45] = unavailable;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] !== t16 || $[48] !== t17 || $[49] !== t18) {
    t19 = <TableScrollArea className="grid h-full w-full">{t16}{t17}{t18}</TableScrollArea>;
    $[47] = t16;
    $[48] = t17;
    $[49] = t18;
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  let t20;
  if ($[51] !== canNextPage || $[52] !== canPreviousPage || $[53] !== error || $[54] !== loading || $[55] !== nextPage || $[56] !== pageIndex || $[57] !== prefetchNext || $[58] !== previousPage) {
    t20 = <CursorPagination loading={loading} error={error} pageIndex={pageIndex} previousPage={previousPage} nextPage={nextPage} canPreviousPage={canPreviousPage} canNextPage={canNextPage} onPrefetchNext={prefetchNext} />;
    $[51] = canNextPage;
    $[52] = canPreviousPage;
    $[53] = error;
    $[54] = loading;
    $[55] = nextPage;
    $[56] = pageIndex;
    $[57] = prefetchNext;
    $[58] = previousPage;
    $[59] = t20;
  } else {
    t20 = $[59];
  }
  let t21;
  if ($[60] !== t15 || $[61] !== t19 || $[62] !== t20) {
    t21 = <div className="gap-lg flex h-full w-full flex-col"><div className="gap-xs flex flex-col">{t15}{t19}{t20}</div></div>;
    $[60] = t15;
    $[61] = t19;
    $[62] = t20;
    $[63] = t21;
  } else {
    t21 = $[63];
  }
  let t22;
  if ($[64] !== t14 || $[65] !== t21) {
    t22 = <VaultTransactionsLayout title="Your transactions" tableSettingsSlot={t14}>{t21}</VaultTransactionsLayout>;
    $[64] = t14;
    $[65] = t21;
    $[66] = t22;
  } else {
    t22 = $[66];
  }
  return t22;
};
export default VaultYourTransactionsClient;
function _temp(transactionType) {
  return TransactionType[transactionType as keyof typeof TransactionType];
}
function _temp2(headerGroup) {
  return <TrHeaderGroup key={headerGroup.id} headerGroup={headerGroup} />;
}
function _temp3(row) {
  return <TrBodyRow key={`${row.id}-${row.original.type}`} row={row} />;
}
function _temp4(row_0, index) {
  return <TrBodyRow key={`skeleton-${index}`} skeletonRow={row_0} />;
}